import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNotify } from "@imminently/react/store/notifications";
import i18n from "@/i18n";

import type {
  ExternalRegisterAdminService,
  ExternalRegisterCreateEntryRequest,
  ExternalRegisterEntriesResponse,
  ExternalRegisterEntryRecord,
  ExternalRegisterMaintenanceEntry,
  ExternalRegisterMaintenanceFilters,
  ExternalRegisterSettings,
  ExternalRegisterSettingsPatch,
} from "./externalRegisterAdminTypes";
import {
  DEFAULT_EXTERNAL_REGISTER_ADMIN_FILTERS,
} from "./externalRegisterAdminTypes";
import { externalRegisterAdminService } from "./externalRegisterAdminService";

type ExternalRegisterAdminContextValue = {
  service: ExternalRegisterAdminService;
  filters: ExternalRegisterMaintenanceFilters;
  setFilters: (filters: ExternalRegisterMaintenanceFilters) => void;
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  entries: ExternalRegisterMaintenanceEntry[];
  total: number;
  sampleDataActive: boolean;
  settings: ExternalRegisterSettings | null;
  loadingEntries: boolean;
  loadingSettings: boolean;
  saving: boolean;
  errorMessage: string | null;
  clearError: () => void;
  refreshEntries: () => Promise<void>;
  refreshSettings: () => Promise<void>;
  createEntry: (payload: ExternalRegisterCreateEntryRequest) => Promise<void>;
  updateEntry: (entryId: string, payload: Partial<ExternalRegisterEntryRecord>) => Promise<void>;
  inactivateEntry: (entryId: string, reason?: string) => Promise<void>;
  reactivateEntry: (entryId: string, reason?: string) => Promise<void>;
  unlinkEntry: (entryId: string, reason: string) => Promise<void>;
  relinkEntry: (entryId: string, patientId: string, reason: string) => Promise<void>;
  deleteNeverLinkedEntry: (entryId: string) => Promise<void>;
  updateSettings: (patch: ExternalRegisterSettingsPatch) => Promise<void>;
};

const ExternalRegisterAdminContext = createContext<ExternalRegisterAdminContextValue | null>(null);

const toSafeErrorMessage = (): string => {
  return i18n.t("externalRegister.admin.provider.error");
};

export const filterExternalRegisterMaintenanceEntries = (
  entries: ExternalRegisterMaintenanceEntry[],
  filters: ExternalRegisterMaintenanceFilters,
): ExternalRegisterMaintenanceEntry[] => {
  const patientId = filters.patientId.trim().toLowerCase();

  return entries
    .filter((entry) => filters.status === "all" || entry.status === filters.status)
    .filter((entry) => filters.linkStatus === "all" || entry.linkStatus === filters.linkStatus)
    .filter((entry) => filters.type === "all" || entry.type === filters.type)
    .filter((entry) => {
      if (!patientId) return true;
      return [
        entry.patientId,
        entry.linkedPatientId,
        entry.previousLinkedPatientId,
      ].some((value) => value?.toLowerCase().includes(patientId));
    });
};

export const ExternalRegisterAdminProvider = ({
  children,
  service = externalRegisterAdminService,
}: {
  children: ReactNode;
  service?: ExternalRegisterAdminService;
}) => {
  const notify = useNotify();
  const [filters, setFiltersState] = useState<ExternalRegisterMaintenanceFilters>(DEFAULT_EXTERNAL_REGISTER_ADMIN_FILTERS);
  const [page, setPageState] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(10);
  const [entriesResponse, setEntriesResponse] = useState<ExternalRegisterEntriesResponse>({ total: 0, data: [] });
  const [settings, setSettings] = useState<ExternalRegisterSettings | null>(null);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const refreshEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const response = await service.listEntries(filters, {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      });
      setEntriesResponse(response);
      setErrorMessage(null);
    } catch {
      setErrorMessage(toSafeErrorMessage());
    } finally {
      setLoadingEntries(false);
    }
  }, [filters, page, rowsPerPage, service]);

  const refreshSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const response = await service.getSettings();
      setSettings(response);
      setErrorMessage(null);
    } catch {
      setErrorMessage(toSafeErrorMessage());
    } finally {
      setLoadingSettings(false);
    }
  }, [service]);

  useEffect(() => {
    void refreshEntries();
  }, [refreshEntries]);

  useEffect(() => {
    void refreshSettings();
  }, [refreshSettings]);

  const withMutation = useCallback(async (mutation: () => Promise<void>) => {
    setSaving(true);
    try {
      await mutation();
      setErrorMessage(null);
      await refreshEntries();
    } catch {
      const message = toSafeErrorMessage();
      setErrorMessage(message);
      notify.error(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  }, [notify, refreshEntries]);

  const setFilters = useCallback((nextFilters: ExternalRegisterMaintenanceFilters) => {
    setPageState(0);
    setFiltersState(nextFilters);
  }, []);

  const setPage = useCallback((nextPage: number) => {
    setPageState(nextPage);
  }, []);

  const setRowsPerPage = useCallback((nextRowsPerPage: number) => {
    setPageState(0);
    setRowsPerPageState(nextRowsPerPage);
  }, []);

  const value = useMemo<ExternalRegisterAdminContextValue>(() => {
    return {
      service,
      filters,
      setFilters,
      page,
      rowsPerPage,
      setPage,
      setRowsPerPage,
      entries: entriesResponse.data,
      total: entriesResponse.total,
      sampleDataActive: false,
      settings,
      loadingEntries,
      loadingSettings,
      saving,
      errorMessage,
      clearError: () => setErrorMessage(null),
      refreshEntries,
      refreshSettings,
      createEntry: (payload) => withMutation(() => service.createEntry(payload).then(() => undefined)),
      updateEntry: (entryId, payload) => withMutation(() => service.updateEntry(entryId, payload).then(() => undefined)),
      inactivateEntry: (entryId, reason) => withMutation(() => service.inactivateEntry(entryId, reason?.trim() ? { reason: reason.trim() } : {}).then(() => undefined)),
      reactivateEntry: (entryId, reason) => withMutation(() => service.reactivateEntry(entryId, reason?.trim() ? { reason: reason.trim() } : {}).then(() => undefined)),
      unlinkEntry: (entryId, reason) => withMutation(() => service.unlinkEntry(entryId, { reason: reason.trim() }).then(() => undefined)),
      relinkEntry: (entryId, patientId, reason) => withMutation(() => service.relinkEntry(entryId, { patientId: patientId.trim(), reason: reason.trim() }).then(() => undefined)),
      deleteNeverLinkedEntry: (entryId) => withMutation(() => service.deleteNeverLinkedEntry(entryId).then(() => undefined)),
      updateSettings: async (patch) => {
        setSaving(true);
        try {
          const nextSettings = await service.updateSettings(patch);
          setSettings(nextSettings);
          setErrorMessage(null);
        } catch {
          setErrorMessage(toSafeErrorMessage());
          throw new Error(toSafeErrorMessage());
        } finally {
          setSaving(false);
        }
      },
    };
  }, [
    entriesResponse.data,
    entriesResponse.total,
    errorMessage,
    filters,
    loadingEntries,
    loadingSettings,
    page,
    refreshEntries,
    refreshSettings,
    rowsPerPage,
    saving,
    service,
    setFilters,
    setPage,
    setRowsPerPage,
    settings,
    withMutation,
  ]);

  return (
    <ExternalRegisterAdminContext.Provider value={value}>
      {children}
    </ExternalRegisterAdminContext.Provider>
  );
};

export const useExternalRegisterAdmin = (): ExternalRegisterAdminContextValue => {
  const context = useContext(ExternalRegisterAdminContext);
  if (!context) {
    throw new Error(i18n.t("externalRegister.admin.provider.hookError"));
  }
  return context;
};
